import React, { useState } from 'react';
import { useSubscriptions } from '../../hooks/useSubscriptions';
import { formatCurrency, formatNumber } from '../../utils/helpers';
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorMessage from '../common/ErrorMessage';
import { trackPlanSelection } from '../../services/analyticsService';

const PricingSection = () => {
    const { plans, monthlyPlans, isLoading, isError, error, loadPlans, switchBillingCycle, getCorrespondingPlan } = useSubscriptions();
    const [isYearly, setIsYearly] = useState(false);

    // Handle billing cycle toggle
    const handleBillingCycleToggle = async () => {
        const newCycle = isYearly ? 'Monthly' : 'Yearly';
        setIsYearly(!isYearly);
        await switchBillingCycle(newCycle);
    };

    // Calculate annual savings percentage
    const calculateAnnualSavings = (monthlyPrice, yearlyPrice) => {
        if (monthlyPrice === 0 || yearlyPrice === 0) return 0;
        const annualMonthlyCost = monthlyPrice * 12;
        const savings = ((annualMonthlyCost - yearlyPrice) / annualMonthlyCost) * 100;
        return Math.round(savings);
    };

    // Calculate maximum savings across all plans
    const calculateMaxSavings = () => {
        if (!isYearly || plans.length === 0 || monthlyPlans.length === 0) return 0;
        
        let maxSavings = 0;
        plans.forEach(plan => {
            if (plan.base_price > 0) {
                // Get the corresponding monthly plan for real savings calculation
                const monthlyPlan = getCorrespondingPlan(plan, 'Monthly');
                if (monthlyPlan && monthlyPlan.base_price > 0) {
                    const savings = calculateAnnualSavings(monthlyPlan.base_price, plan.base_price);
                    if (savings > maxSavings) {
                        maxSavings = savings;
                    }
                }
            }
        });
        
        return maxSavings;
    };

    // Get features for a plan
    const getFeatures = (plan) => {
        // Use features from API if available
        if (plan.features && plan.features.length > 0) {
            return plan.features;
        }
        
        // Fallback features based on plan data
        const features = [
            `Up to ${formatNumber(plan.user_limit)} users`,
            `${formatNumber(plan.ai_tokens_limit)} AI tokens/${plan.billing_cycle === 'Yearly' ? 'year' : 'month'}`,
            'Dashboard Analytics'
        ];

        // Add support level based on plan
        if (plan.plan_name.toLowerCase().includes('pro') || plan.plan_name.toLowerCase().includes('enterprise')) {
            features.push('Priority Support');
        } else {
            features.push('Email Support');
        }

        // Add enterprise features
        if (plan.plan_name.toLowerCase().includes('enterprise')) {
            features.push('Dedicated Account Manager');
        }

        return features;
    };

    // Get plan badge
    const getPlanBadge = (planName) => {
        if (planName.toLowerCase().includes('pro')) return 'Most Popular';
        if (planName.toLowerCase().includes('enterprise')) return 'Best Value';
        return null;
    };

    // Get pricing display text
    const getPricingDisplay = (plan) => {
        const price = plan.base_price;
        const currency = plan.currency || 'USD';
        const cycle = plan.billing_cycle;
        
        if (price === 0) {
            return 'Free';
        }
        
        const formattedPrice = formatCurrency(price, currency);
        const period = cycle === 'Yearly' ? '/year' : '/month';
        
        return `${formattedPrice}${period}`;
    };

    if (isLoading) {
        return (
            <section id="pricing" className="section price-plan-area bg-gray overflow-hidden ptb_100">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-12 col-md-10 col-lg-8">
                            <div className="section-heading text-center">
                                <h2>Simple, Transparent Pricing</h2>
                                <p className="d-none d-sm-block mt-4">Choose the plan that fits your business needs. All plans include our core features with different usage limits.</p>
                            </div>
                        </div>
                    </div>
                    <div className="row justify-content-center">
                        <div className="col-12">
                            <LoadingSpinner size="lg" />
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    if (isError) {
        return (
            <section id="pricing" className="section price-plan-area bg-gray overflow-hidden ptb_100">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-12 col-md-10 col-lg-8">
                            <div className="section-heading text-center">
                                <h2>Simple, Transparent Pricing</h2>
                                <p className="d-none d-sm-block mt-4">Choose the plan that fits your business needs. All plans include our core features with different usage limits.</p>
                            </div>
                        </div>
                    </div>
                    <div className="row justify-content-center">
                        <div className="col-12">
                            <ErrorMessage error={error} onRetry={loadPlans} />
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section id="pricing" className="section price-plan-area bg-gray overflow-hidden ptb_100">
            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-12 col-md-10 col-lg-8">
                        {/* Section Heading */}
                        <div className="section-heading text-center">
                            <h2>Simple, Transparent Pricing</h2>
                            <p className="d-none d-sm-block mt-4">Choose the plan that fits your business needs. All plans include our core features with different usage limits.</p>
                            <p className="d-block d-sm-none mt-4">Choose the plan that fits your business needs.</p>
                            
                            {/* Billing Cycle Toggle */}
                            <div className="billing-cycle-toggle mt-5">
                                <div className="d-flex flex-column align-items-center justify-content-center">
                                    {/* Savings Badge - positioned above toggle */}
                                    {isYearly && (
                                        <div className="savings-badge mb-3">
                                            <span className="badge bg-success text-white px-3 py-2 fw-bold" style={{ fontSize: '12px' }}>
                                                Save Up To {calculateMaxSavings()}%
                                            </span>
                                        </div>
                                    )}
                                    
                                    {/* Toggle Switch */}
                                    <div className="toggle-container">
                                        <div className="toggle-background d-flex align-items-center bg-light rounded-pill p-1 shadow-sm" style={{ 
                                            width: 'min(200px, 90vw)', 
                                            height: '40px',
                                            maxWidth: '200px'
                                        }}>
                                            <button
                                                className={`toggle-option border-0 rounded-pill px-2 px-sm-4 py-2 ${!isYearly ? 'bg-primary text-white fw-bold shadow-sm' : 'bg-transparent text-muted'}`}
                                                style={{ 
                                                    width: '50%', 
                                                    height: '32px',
                                                    transition: 'all 0.3s ease',
                                                    position: 'relative',
                                                    zIndex: 2,
                                                    fontSize: 'clamp(12px, 3vw, 14px)',
                                                    whiteSpace: 'nowrap'
                                                }}
                                                onClick={() => handleBillingCycleToggle()}
                                            >
                                                Monthly
                                            </button>
                                            <button
                                                className={`toggle-option border-0 rounded-pill px-2 px-sm-4 py-2 ${isYearly ? 'bg-primary text-white fw-bold shadow-sm' : 'bg-transparent text-muted'}`}
                                                style={{ 
                                                    width: '50%', 
                                                    height: '32px',
                                                    transition: 'all 0.3s ease',
                                                    position: 'relative',
                                                    zIndex: 2,
                                                    fontSize: 'clamp(12px, 3vw, 14px)',
                                                    whiteSpace: 'nowrap'
                                                }}
                                                onClick={() => handleBillingCycleToggle()}
                                            >
                                                Annually
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="row justify-content-center">
                    <div className="col-12">
                        <div className="row price-plan-wrapper">
                            {plans.map((plan, idx) => {
                                const badge = getPlanBadge(plan.plan_name);
                                const features = getFeatures(plan);
                                const isPopular = plan.plan_name.toLowerCase().includes('pro');
                                const pricingDisplay = getPricingDisplay(plan);

                                return (
                                    <div key={`p_${idx}`} className="col-12 col-md-6 col-lg-3 mt-4">
                                        {/* Single Price Plan */}
                                        <div className={`single-price-plan text-center p-3 p-sm-4 ${isPopular ? 'border-primary' : ''}`} style={{ minHeight: '400px' }}>
                                            {/* Badge */}
                                            {badge && (
                                                <div className="badge-container mb-2">
                                                    <span className="badge bg-primary text-white px-2 py-1 rounded-pill small">
                                                        {badge}
                                                    </span>
                                                </div>
                                            )}
                                            
                                            {/* Plan Title */}
                                            <div className="plan-title my-2">
                                                <h5 className="text-uppercase mb-0" style={{ fontSize: 'clamp(16px, 4vw, 18px)' }}>{plan.plan_name}</h5>
                                            </div>
                                            
                                            {/* Plan Price */}
                                            <div className="plan-price pb-2">
                                                <h3 className="fw-7 mb-0" style={{ fontSize: 'clamp(20px, 5vw, 24px)' }}>
                                                    {pricingDisplay}
                                                </h3>
                                                {plan.billing_cycle === 'Yearly' && plan.base_price > 0 && (
                                                    <div className="annual-savings mt-2">
                                                        <span className="badge bg-success text-white" style={{ fontSize: 'clamp(10px, 2.5vw, 12px)' }}>
                                                            Save {(() => {
                                                                // Get the corresponding monthly plan for real savings calculation
                                                                const monthlyPlan = getCorrespondingPlan(plan, 'Monthly');
                                                                if (monthlyPlan && monthlyPlan.base_price > 0) {
                                                                    return calculateAnnualSavings(monthlyPlan.base_price, plan.base_price);
                                                                }
                                                                return 0;
                                                            })()}%
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                            
                                            {/* Plan Description */}
                                            {plan.description && (
                                                <div className="plan-description mb-3">
                                                    <p className="small text-muted" style={{ fontSize: 'clamp(12px, 3vw, 14px)' }}>{plan.description}</p>
                                                </div>
                                            )}
                                            
                                            {/* Plan Features */}
                                            <div className="plan-description">
                                                <ul className="plan-features list-unstyled">
                                                    {features.map((feature, index) => (
                                                        <li key={index} className="border-top py-2">
                                                            <i className="fas fa-check text-success me-2 small"></i>
                                                            <span className="small" style={{ fontSize: 'clamp(12px, 3vw, 14px)' }}>{feature}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                            
                                            {/* Plan Button */}
                                            <div className="plan-button mt-4">
                                                <a 
                                                    href={`/signup?plan=${plan.id}&billing_cycle=${plan.billing_cycle}`}
                                                    className={`btn ${isPopular ? 'btn-primary' : 'btn-outline-primary'}`}
                                                    style={{ 
                                                        fontSize: 'clamp(12px, 3vw, 14px)',
                                                        padding: '8px 16px',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                    onClick={() => {
                                                        console.log('Pricing: Clicking plan:', {
                                                            id: plan.id,
                                                            name: plan.plan_name,
                                                            billing_cycle: plan.billing_cycle,
                                                            url: `/signup?plan=${plan.id}&billing_cycle=${plan.billing_cycle}`
                                                        });
                                                        trackPlanSelection(plan.plan_name, plan.base_price, plan.billing_cycle);
                                                    }}
                                                >
                                                    {plan.base_price === 0 ? 'Start Free' : 'Get Started'}
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default PricingSection;